import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Button,
  CloseButton,
  Alert,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import "../styles/AuthModal.css";
import { FaUser, FaCheckCircle, FaUsers, FaEye, FaEyeSlash } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { useAuth } from "../Auth/AuthContext";
import { loginUser, signupUser } from "../Api/authApi";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../Api/apiConfig";
import OrganizerOnboardModal from "./OrganizerOnboardModal";
import ForgotPasswordModal from "./ForgotPasswordModal";
import ResendVerificationModal from "./ResendVerificationModal";

const AuthModal = ({ show, handleClose, isLogin, setIsLogin }) => {
  const toggleForm = () => setIsLogin(!isLogin);
  const [selectedRole, setSelectedRole] = useState("attendee");
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showResendVerificationModal, setShowResendVerificationModal] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [verifyEmailInput, setVerifyEmailInput] = useState("");
  const { handleLogin } = useAuth(); // from context
  const navigate = useNavigate();

  const handleOpenVerify = () => {
    setVerifyEmailInput(formData.loginEmail || formData.signupEmail || "");
    setShowResendVerificationModal(true);
    handleClose(); // Close the AuthModal
  };

  const handleGoogleLogin = () => {
    window.location.href = `${BASE_URL}/accounts/google/`;
  };

  // Reset form data when modal is closed
  useEffect(() => {
    if (!show) {
      setFormData({});
      setError("");
      setLoading(false);
      setShowLoginPassword(false);
      setShowSignupPassword(false);
      setShowConfirmPassword(false);
      setSelectedRole("attendee");
    }
  }, [show]);

  // ✅ handle input changes
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
  };

  // ✅ handle phone number changes - filter out non-numeric characters
  const handlePhoneChange = (e) => {
    // Filter out any non-numeric characters and limit to 10 digits
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, [e.target.id]: value });
  };

  // Password visibility toggle functions
  const toggleLoginPassword = () => setShowLoginPassword(!showLoginPassword);
  const toggleSignupPassword = () => setShowSignupPassword(!showSignupPassword);
  const toggleConfirmPassword = () => setShowConfirmPassword(!showConfirmPassword);

  // ✅ handle login/signup submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isLogin) {
        // LOGIN
        await loginUser(formData.loginEmail, formData.loginPassword);
        const { profile } = await handleLogin(formData.loginEmail, formData.loginPassword); // refresh context
        toast.success("Logged in successfully!");

        setTimeout(() => {
          handleClose();
          setFormData({});

          if (profile?.role === 'admin') {
            navigate('/control');
          } else if (profile?.role === 'organizer') {
            navigate('/dashboard');
          }
        }, 300);
      } else {
        // SIGNUP - Updated format to match API requirement
        const newUser = {
          email: formData.signupEmail,
          password: formData.signupPassword,
          name: formData.signupName,
          phone: parseInt(formData.signupPhone),
        };
        await signupUser(newUser);
        if (selectedRole === "organizer") {
          // Auto-login after signup for organizer
          await loginUser(formData.signupEmail, formData.signupPassword);
          await handleLogin(formData.signupEmail, formData.signupPassword);
          toast.success("Signed up successfully! Now set up your organizer profile.");
          setTimeout(() => {
            setShowOnboardModal(true);
          }, 300);
        } else {
          toast.success("Signed up successfully! You can now log in.");
          setTimeout(() => setIsLogin(true), 1200);
        }
      }
    } catch (err) {
      if (err.data && typeof err.data === 'object') {
        // Handle field-specific errors (e.g., { phone: ["msg"], email: ["msg"] })
        const fieldErrors = Object.entries(err.data)
          .map(([field, messages]) => {
            const fieldName = field.charAt(0).toUpperCase() + field.slice(1);
            const msg = Array.isArray(messages) ? messages[0] : messages;
            return `${fieldName}: ${msg}`;
          })
          .join(" ");
        setError(fieldErrors || err.message);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="custom-modal-size"
        contentClassName="auth-modal-content"
        scrollable={!isLogin}
      >
        <Modal.Body className="auth-modal-body">
          <CloseButton onClick={handleClose} className="close-btn" />

          <div className="heading text-center mb-4">
            <h1 className="title">{isLogin ? "Welcome Back" : "Join Tixoza"}</h1>
            <h5 className="subtitle">
              {isLogin
                ? "Sign in to your Tixoza account"
                : "Create your account to get started"}
            </h5>
          </div>

          {error && <Alert variant="danger" className="py-2 px-3 mx-auto text-center" style={{ borderRadius: "12px", width: "fit-content", fontSize: "0.95rem" }}>{error}</Alert>}

          <Form onSubmit={handleSubmit}>
            {isLogin ? (
              <>
                {/* ---------------- LOGIN FORM ---------------- */}
                <Form.Group className="mb-3" controlId="loginEmail">
                  <Form.Label>Email Address</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    onChange={handleChange}
                    value={formData.loginEmail || ""}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3" controlId="loginPassword">
                  <Form.Label>Password</Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type={showLoginPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      onChange={handleChange}
                      value={formData.loginPassword || ""}
                      required
                    />
                    <Button
                      variant="link"
                      className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent"
                      onClick={toggleLoginPassword}
                      style={{ zIndex: 1 }}
                    >
                      {showLoginPassword ? <FaEyeSlash color="#8B2EE2" /> : <FaEye color="#8B2EE2" />}
                    </Button>
                  </div>
                </Form.Group>

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <a onClick={handleOpenVerify} className="text-purple pointer">
                    Verify Email
                  </a>
                  <a onClick={() => setShowForgotPasswordModal(true)} className="text-purple pointer">
                    Forgot Password?
                  </a>
                </div>

                {/* <div className="d-flex justify-content-center align-items-center"> */}
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <Button
                      type="submit"
                      className="btn-gradient w-100 h-100 d-flex align-items-center justify-content-center"
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" /> : "Log In"}
                    </Button>
                  </div>
                  <div className="col-6">
                    <Button
                      variant="outline-secondary"
                      className="social-login-btn w-100 h-100 m-0"
                      onClick={handleGoogleLogin}
                    >
                      <FcGoogle className="me-2" size={20} /> Google
                    </Button>
                  </div>
                </div>

                {/* <div className="text-center mb-3">or continue with</div>
              <div className="d-flex justify-content-center gap-2">
                <Button variant="outline-secondary" className="flex social-btn">
                  <img src={google_icon} className="google-logo" alt="g-logo" />{" "}
                  Google
                </Button>
                <Button
                  variant="outline-secondary"
                  className="flex-fill social-btn"
                >
                  <FaApple className="me-2" /> Apple
                </Button>
              </div> */}

                <div className="text-center mt-3">
                  Don't have an account?{" "}
                  <p onClick={toggleForm} className="text-purple pointer">
                    Sign Up
                  </p>
                </div>
              </>
            ) : (
              <>
                {/* ---------------- SIGNUP FORM ---------------- */}

                {/* Full Name + Email in one row */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Form.Group controlId="signupName">
                      <Form.Label>Full Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter your full name"
                        onChange={handleChange}
                        value={formData.signupName || ""}
                        required
                      />
                    </Form.Group>
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Group controlId="signupEmail">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Enter your email"
                        onChange={handleChange}
                        value={formData.signupEmail || ""}
                        required
                      />
                    </Form.Group>
                  </div>
                </div>

                {/* Phone (Full Width) */}
                <Form.Group className="mb-3" controlId="signupPhone">
                  <Form.Label>Phone Number</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter your phone number"
                    onChange={handlePhoneChange}
                    value={formData.signupPhone || ""}
                    required
                    maxLength={10}
                    minLength={10}
                  />
                </Form.Group>

                {/* Password + Confirm Password in one row */}
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <Form.Group controlId="signupPassword">
                      <Form.Label>Password</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type={showSignupPassword ? "text" : "password"}
                          placeholder="Create a password"
                          onChange={handleChange}
                          value={formData.signupPassword || ""}
                          required
                        />
                        <Button
                          variant="link"
                          className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent"
                          onClick={toggleSignupPassword}
                          style={{ zIndex: 1 }}
                        >
                          {showSignupPassword ? <FaEyeSlash color="#8B2EE2" /> : <FaEye color="#8B2EE2" />}
                        </Button>
                      </div>
                    </Form.Group>
                  </div>

                  <div className="col-md-6 mb-3">
                    <Form.Group controlId="signupConfirmPassword">
                      <Form.Label>Confirm Password</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Confirm your password"
                          onChange={handleChange}
                          value={formData.signupConfirmPassword || ""}
                          required
                        />
                        <Button
                          variant="link"
                          className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent"
                          onClick={toggleConfirmPassword}
                          style={{ zIndex: 1 }}
                        >
                          {showConfirmPassword ? <FaEyeSlash color="#8B2EE2" /> : <FaEye color="#8B2EE2" />}
                        </Button>
                      </div>
                    </Form.Group>
                  </div>
                </div>

                {/* ROLE SELECTION */}
                <Form.Label>Select Your Role</Form.Label>

                <div className="role-cards mb-3 mt-3">
                  {/* Attendee Card */}
                  <div
                    className={`role-card ${selectedRole === "attendee" ? "active" : ""
                      }`}
                    onClick={() => setSelectedRole("attendee")}
                  >
                    <div className="role-header">
                      <div className="small-icon-circle organizer-icon-circle">
                        <FaUser />
                      </div>
                      <h5 className="role-title">Attendee</h5>
                    </div>

                    {selectedRole === "attendee" && (
                      <div className="role-check">
                        <FaCheckCircle />
                      </div>
                    )}
                  </div>

                  {/* Organizer Card */}
                  <div
                    className={`role-card ${selectedRole === "organizer" ? "active" : ""
                      }`}
                    onClick={() => setSelectedRole("organizer")}
                  >
                    <div className="role-header">
                      <div className="small-icon-circle organizer-icon-circle">
                        <FaUsers />
                      </div>
                      <h5 className="role-title">Organizer</h5>
                    </div>

                    {selectedRole === "organizer" && (
                      <div className="role-check">
                        <FaCheckCircle />
                      </div>
                    )}
                  </div>
                </div>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="I agree to the Terms of Service and Privacy Policy"
                    required
                  />
                </Form.Group>

                <div className="row g-2 mb-3 mt-4">
                  <div className="col-6">
                    <Button
                      type="submit"
                      className="btn-gradient w-100 h-100 d-flex align-items-center justify-content-center"
                      disabled={loading}
                    >
                      {loading ? <Spinner size="sm" /> : "Sign Up"}
                    </Button>
                  </div>
                  <div className="col-6">
                    <Button
                      variant="outline-secondary"
                      className="social-login-btn w-100 h-100 m-0"
                      onClick={handleGoogleLogin}
                    >
                      <FcGoogle className="me-2" size={20} /> Google
                    </Button>
                  </div>
                </div>

                {/* <div className="d-flex justify-content-center gap-2">
                <Button variant="outline-secondary" className="flex social-btn">
                  <img src={google_icon} className="google-logo" alt="g-logo" />{" "}
                  Google
                </Button>
              </div> */}

                <div className="text-center mt-2">
                  Already have an account?{" "}
                  <p onClick={toggleForm} className="text-purple pointer">
                    Log In
                  </p>
                </div>
              </>
            )}
          </Form>
        </Modal.Body>
      </Modal>

      <OrganizerOnboardModal
        show={showOnboardModal}
        handleClose={() => setShowOnboardModal(false)}
        onOnboarded={async () => {
          // Refetch profile after onboarding to get updated role
          await handleLogin(formData.signupEmail, formData.signupPassword);
          setIsLogin(true);
          handleClose();
        }}
      />

      <ForgotPasswordModal
        show={showForgotPasswordModal}
        handleClose={() => setShowForgotPasswordModal(false)}
      />

      <ResendVerificationModal
        show={showResendVerificationModal}
        handleClose={() => setShowResendVerificationModal(false)}
        initialEmail={verifyEmailInput}
      />
    </>
  );
};

export default AuthModal;
